// src/api/overtime.ts
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const OVERTIME_BASE_URL = `${API_BASE_PATH}/OT`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ✅ POST payload
export interface OvertimeRequest {
  date_apply: string; // YYYY-MM-DD
  start_time: string; // HH:mm:ss
  end_time: string; // HH:mm:ss
  total_hours: string; // HH:mm:ss
  no_of_days: "Full Day" | "Half Day";
  reason: string;
}

// 📦 PUT payload
export interface OvertimeUpdate {
  overtime_id: number;
  status: "Approved" | "Rejected";
  reject_reason?: string;
}

// 📥 Response record
export interface OvertimeRecord {
  overtime_id: number;
  employee_id: string;
  date_apply: string;
  start_time: string;
  end_time: string;
  total_hours: string;
  no_of_days: "Full Day" | "Half Day";
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason: string | null;
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
  updated_at: string | null;
}

// 🌟 POST - Create an overtime request
export const postOvertime = async (
  data: OvertimeRequest,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.post(
      `${OVERTIME_BASE_URL}/`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Overtime submitted successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✏️ PUT - Update OT status
export const updateOvertimeStatus = async (
  data: OvertimeUpdate,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.put(
      `${OVERTIME_BASE_URL}/`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Overtime status updated");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🙋‍♂️ GET - Get own OT records
export const getSelfOvertime = async (token: string | null): Promise<OvertimeRecord[]> => {
  try {
    const response = await axios.get(`${OVERTIME_BASE_URL}/self/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🌐 GET - Get all OT records (admin use)
export const getAllOvertime = async (token: string | null): Promise<OvertimeRecord[]> => {
  try {
    const response = await axios.get(`${OVERTIME_BASE_URL}/all/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 📊 GET - Get widgets data with filters
export const getOvertimeWidgets = async (
  token: string | null,
  params: {
    month: number;
    year: number;
    day?: number;
    employee_id?: string;
  }
): Promise<any> => {
  try {
    const searchParams = new URLSearchParams();

    searchParams.append("month", params.month.toString());
    searchParams.append("year", params.year.toString());
    if (params.day !== undefined) {
      searchParams.append("day", params.day.toString());
    }
    if (params.employee_id !== undefined) {
      searchParams.append("employee_id", params.employee_id);
    }

    const response = await axios.get(`${OVERTIME_BASE_URL}/widgets?${searchParams.toString()}`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};
